/**
 * PRODUCT-IDENTITY-1 — the single lookup for which product's name, value
 * proposition, and feature lines a shared component (Login, TosGate,
 * GettingStarted, RequestAccess, ForgotPassword, ResetPassword) renders.
 * Those six components live in frontend/src/ once and are routed into all
 * three product SPAs (router/index.tsx = QoE, router/attest.tsx,
 * router/valuation.tsx) — before this module they all hardcoded "QoE
 * Platform", including in the one place that records a legal acceptance.
 *
 * Signal choice: VITE_PRODUCT, baked via `define` in each product's OWN Vite
 * config — vite.config.ts → 'qoe', vite.config.attest.ts → 'attest',
 * vite.config.valuation.ts → 'valuation' (vitest.config.ts → 'qoe', since
 * QoE is the shared-test default and the regression fence for this package).
 * NOT YANSAR_APP_MODE: that env var is only set in each Dockerfile's Stage 2
 * (the Python/FastAPI stage), after Stage 1's `npm run build` has already
 * produced the bundle — it is not on process.env when Vite evaluates this
 * build, and reading it here would mean duplicating it into Stage 1 too. The
 * three-way vite.config.<product>.ts split already IS the per-product build
 * signal; this reuses it the same way vite.config.ts already bakes
 * VITE_APP_VERSION via `define`, rather than adding a fourth env var.
 *
 * No fallback arm: an unregistered or blank product fails loudly at import
 * time (see resolveProductKey) instead of silently rendering QoE's identity
 * — that silent fallback is how six components ended up here in the first
 * place.
 */

export type ProductKey = 'qoe' | 'valuation' | 'attest' | 'otcid'

export interface ProductIdentity {
  readonly headline: string
  readonly subhead: string
  readonly features: readonly [string, string, string]
  /** PLAT-AUTH-UI-ONE-2: where a signed-in person of this product lands once
   *  first sign-in is done (GettingStarted). Each build's own home route. */
  readonly home: string
}

// Owner-approved 2026-09-01 (PRODUCT-IDENTITY-1 §3), verbatim. QoE's row is
// its current wording, unchanged — spelled out explicitly rather than left
// as an implicit default, so the lookup has three real arms and none of them
// is a silent fallback.
const PRODUCT_IDENTITY: Record<ProductKey, ProductIdentity> = {
  qoe: {
    headline: 'Quality of Earnings Platform',
    subhead: 'M&A due diligence tools for accounting professionals.',
    features: ['Bank-grade document security', 'AI-powered financial analysis', 'Audit-ready QoE reports'],
    home: '/dashboard',
  },
  valuation: {
    headline: 'Business Valuation',
    subhead: 'Defensible business valuations for CPAs and owners.',
    features: ['SSVS-compliant reporting', 'Calculation and conclusion of value', 'Signed, audit-ready workpapers'],
    home: '/valuation',
  },
  attest: {
    headline: 'Attest',
    subhead: 'Verification letters lenders will accept.',
    features: ['CPA-signed verification', 'Third-party ready', 'Audit trail on every letter'],
    home: '/attest/letters',
  },
  // PLAT-AUTH-UI-ONE-2: OTCID's sign-in screens are GENERATED from this
  // package (scripts/generate_auth_screens.py), so OTCID registers here like
  // the other three. Wording is OTCID's existing sign-in panel text, verbatim
  // (apps/otcid_react/src/pages/SignIn.tsx before this lane).
  otcid: {
    headline: 'OTCID Disclosure Generator',
    subhead: 'OTC Markets disclosure documents for compliance professionals.',
    features: ['All 10 OTC Markets disclosure items', 'Multi-brand white-label platform', 'Compliance-validated output'],
    home: '/',
  },
}

function isProductKey(value: string | undefined): value is ProductKey {
  return value === 'qoe' || value === 'valuation' || value === 'attest' || value === 'otcid'
}

function resolveProductKey(): ProductKey {
  const raw = import.meta.env.VITE_PRODUCT as string | undefined
  if (isProductKey(raw)) return raw
  // Fail loudly (I7) — never silently render QoE's identity to a product
  // that forgot to register, or one whose build signal was blanked out.
  throw new Error(
    `productIdentity: unregistered product "${raw ?? '(unset)'}". VITE_PRODUCT must be baked to ` +
    `'qoe' | 'valuation' | 'attest' | 'otcid' by the build's own vite config ` +
    `(vite.config.ts, vite.config.attest.ts, vite.config.valuation.ts, apps/otcid_react/vite.config.ts, ` +
    `or the matching vitest.config.ts for tests).`
  )
}

export const PRODUCT_KEY: ProductKey = resolveProductKey()
export const PRODUCT: ProductIdentity = PRODUCT_IDENTITY[PRODUCT_KEY]
